import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

// ---------------------------------------------------------------------------
// CRM Connection — One OAuth-linked CRM account per user (e.g. HubSpot portal)
// Tokens are encrypted at rest via token-encryption.ts (AES-256-GCM).
// ---------------------------------------------------------------------------

@Entity()
export class CrmConnection extends MongoEntity {
  static override _class = 'CrmConnection';
  static override _collectionName = 'crm_connections';

  @Field(type => String) userId!: string;

  /** 'hubspot' | (future) 'pipedrive' | 'salesforce' | 'attio' */
  @Field(type => String) provider!: string;

  /** Provider-specific account identifier (e.g. HubSpot portalId / hubId as string) */
  @Field(type => String) accountId?: string;
  /** Human-readable label (HubSpot hub domain, portal name) */
  @Field(type => String) accountLabel?: string;

  /**
   * 'oauth'        — user-linked Public App: refresh token rotation required
   * 'private_app'  — server-configured PAT bearer: no refresh, lives until rotated in HubSpot
   */
  @Field(type => String) authMode?: string;

  /** Encrypted access token (short-lived for OAuth, the PAT itself for private_app) */
  @Field(type => String) encryptedAccessToken!: string;
  /** Encrypted refresh token (only meaningful for OAuth; empty for private_app) */
  @Field(type => String) encryptedRefreshToken!: string;
  /** ISO timestamp when the access token expires (sentinel "9999-12-31T23:59:59Z" for private_app) */
  @Field(type => String) accessTokenExpiresAt!: string;

  /** Granted OAuth scopes (space-separated) */
  @Field(type => String) scopes?: string;

  /** 'active' | 'revoked' | 'error' */
  @Field(type => String) status!: string;
  @Field(type => String) lastError?: string;

  @Field(type => String) connectedAt!: string;
  @Field(type => String) lastUsedAt?: string;

  /**
   * Operator-confirmed Lead Status mapping (Teil 2), as a small JSON object
   * `{ [localStatus]: hubspotOptionValue }` — a missing key means "leave unset".
   * When present it binds AUTHORITATIVELY at send time (the deterministic
   * keyword resolver is only the fallback when no mapping is confirmed), and the
   * runtime then needs no portal-options read at all. Stored as JSON (not nested
   * sub-docs) so writes are a single scalar $set — no ORM sub-document risk.
   */
  @Field(type => String) leadStatusMappingJson?: string;
}
